/**
 * useCaptureArea.ts
 * Zustand store for managing capture area state
 * 
 * States:
 * - isCaptureMode: user is in selection mode (overlay visible)
 * - capturedImage: base64 data URL of the captured region
 * - isModalOpen: whether the preview modal is showing
 */

import { create } from 'zustand';

interface CaptureAreaState {
  // State
  isCaptureMode: boolean;
  capturedImage: string | null;
  isModalOpen: boolean;

  // Actions
  startCapture: () => void;
  cancelCapture: () => void;
  setCapturedImage: (imageDataUrl: string) => void;
  closeModal: () => void;
  reset: () => void;
}

export const useCaptureAreaStore = create<CaptureAreaState>((set) => ({
  // Initial state
  isCaptureMode: false,
  capturedImage: null,
  isModalOpen: false,

  // Start capture mode (show overlay)
  startCapture: () => set({
    isCaptureMode: true,
    capturedImage: null,
    isModalOpen: false,
  }),

  // Cancel capture (Escape key or click outside)
  cancelCapture: () => set({
    isCaptureMode: false,
    capturedImage: null,
    isModalOpen: false,
  }),

  // Set the captured image and open modal
  setCapturedImage: (imageDataUrl: string) => set({
    isCaptureMode: false,
    capturedImage: imageDataUrl,
    isModalOpen: true,
  }),

  // Close the preview modal
  closeModal: () => set({
    isModalOpen: false,
    capturedImage: null,
  }),

  // Full reset
  reset: () => set({
    isCaptureMode: false,
    capturedImage: null,
    isModalOpen: false,
  }),
}));
